import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  Injector,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewEncapsulation,
  afterNextRender,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, debounceTime, finalize, of, switchMap } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { CatalogFilter } from '../../common/CatalogFilter';
import { CATALOG_MAX_PINS, CatalogPin, CatalogPins } from '../../common/CatalogPins';
import { CatalogGeoQuery, GeoPosition } from '../../common/Geo';
import { DistancePipe } from '../../pipes/distance.pipe';
import { CatalogService } from '../../services/catalog.service';
import { MapHandle, MapPin, MapViewport } from './map-adapter';
import { MAP_ADAPTER } from './map-adapter.token';

/**
 * What the map draws pins for: the list's filter and, when the list is located, its origin and
 * radius. A NEW object per search run — the results page builds one in `reload()` — so the map
 * notices a re-run even when the filter's own fields did not change.
 */
export interface CatalogMapQuery {
  name: string | null;
  categoryId: string | null;
  productSkuId: string | null;
  origin: GeoPosition | null;
  radiusKm: number | null;
  /** What the origin marker is called — the chip's label („Mein Standort", „50667 Köln"). */
  originLabel: string | null;
}

/** A pin the user asked to narrow the list to. */
export interface CatalogMapSeller {
  id: string;
  name: string;
}

/** localStorage: the toggle is a layout decision of this device, not a profile preference (#172 decision 3). */
export const MAP_VISIBLE_STORAGE_KEY = 'search.mapVisible';

/** Bootstrap's `md`: below it the map starts collapsed (decided 2026-10-21). */
const WIDE_VIEWPORT_QUERY = '(min-width: 768px)';

/** Pans and zooms settle before the pins are re-queried — one request per gesture, not per step. */
const VIEWPORT_DEBOUNCE_MS = 400;

/** Where the map opens before anything is known: Germany, as a whole. */
const GERMANY_CENTER: GeoPosition = { latitude: 51.16, longitude: 10.45 };
const GERMANY_ZOOM = 6;

/**
 * #172: the map block above the result list — the sellers with matching, physically available
 * listings as one pin each, the searcher's own position, and a popup per pin.
 *
 * Two modes, and the hint line under the map names the one in force. In **area** mode the pins
 * are those of the list's own query (its radius, or the whole result without one) and the map
 * is fitted to it; every new search run (a new `query`) returns to it. A user pan or zoom
 * switches to **viewport** mode: the pins are re-queried for what is on screen
 * (`north/south/east/west`, the origin kept as the distance reference) while the list keeps
 * its radius — the backend answers both out of one candidate pipeline, so the two never
 * disagree about what matched. The backend also decides which sellers get a pin at all
 * (`Available` listings only, capped at 500 with `truncated`); the client never filters pins.
 *
 * The library is loaded the first time the map is shown and never while it is collapsed, and
 * a collapsed map requests no pins either — it stays alive, hidden, because the default
 * provider bills per map instance. The toggle state lives in `localStorage`; below 768 px the
 * map starts collapsed. The list stays the full-value view — the map is a second
 * way in, not the only one — so the toggle is a real button, the map region is labelled, and a
 * pin's content is text in its popup and on the tile.
 */
@Component({
  selector: 'app-catalog-map',
  templateUrl: './catalog-map.component.html',
  styleUrls: ['./catalog-map.component.css'],
  // Leaflet creates the pin and popup elements itself, outside Angular's view, so their styles
  // cannot be scoped: the stylesheet is global and every class is prefixed `catalog-map-`.
  encapsulation: ViewEncapsulation.None,
  providers: [DistancePipe],
})
export class CatalogMapComponent implements AfterViewInit, OnChanges, OnDestroy {
  private readonly adapter = inject(MAP_ADAPTER);
  private readonly catalogService = inject(CatalogService);
  private readonly distancePipe = inject(DistancePipe);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);

  readonly query = input<CatalogMapQuery | null>(null);

  /** „Nur diesen Anbieter zeigen" in a pin's popup. */
  readonly sellerSelected = output<CatalogMapSeller>();

  /** Always in the DOM (hidden while collapsed), so the map can be mounted from a lifecycle hook. */
  private readonly container = viewChild.required<ElementRef<HTMLDivElement>>('mapContainer');

  readonly visible = signal(readStoredVisibility());
  readonly maxPins = CATALOG_MAX_PINS;

  // Template state as signals: every one of them changes outside a user event — in the
  // adapter's promise, in a debounced gesture, in a response — and a signal marks the view
  // dirty wherever it is written, with or without a zone.
  readonly mode = signal<'area' | 'viewport'>('area');
  readonly sellerCount = signal(0);
  readonly truncated = signal(false);
  readonly loading = signal(false);
  readonly failed = signal(false);

  private handle: MapHandle | null = null;
  private creating: Promise<void> | null = null;
  private readonly viewport$ = new Subject<MapViewport>();
  private readonly pinsLoad$ = new Subject<CatalogGeoQuery | null>();

  constructor() {
    this.viewport$
      .pipe(debounceTime(VIEWPORT_DEBOUNCE_MS), takeUntilDestroyed(this.destroyRef))
      .subscribe((viewport) => {
        this.mode.set('viewport');
        this.pinsLoad$.next({ origin: this.query()?.origin ?? null, bounds: viewport });
      });

    // One pipeline, so a gesture that supersedes a request in flight cancels it (`switchMap`)
    // rather than racing it. A failed load is said under the map and nothing more: the pins are
    // a second view of a list that is already on the page, and must not toast over it.
    this.pinsLoad$
      .pipe(
        switchMap((geo) => {
          this.loading.set(true);
          return this.catalogService.getPins(this.filter(), geo).pipe(
            catchError(() => of<CatalogPins | null>(null)),
            finalize(() => this.loading.set(false)),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((result) => this.applyPins(result));
  }

  ngAfterViewInit(): void {
    if (this.visible()) {
      this.mount();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['query']) {
      this.mode.set('area');
      if (this.handle) {
        this.applyArea();
      }
    }
  }

  ngOnDestroy(): void {
    this.handle?.destroy();
    this.handle = null;
  }

  toggle(): void {
    const visible = !this.visible();
    writeStoredVisibility(visible);
    this.visible.set(visible);
    if (visible) {
      // After the next render, when the container has its size again: a map created or resumed
      // inside a hidden element measures a 0×0 viewport and places everything at its top-left.
      afterNextRender(() => this.resume(), { injector: this.injector });
    }
    // Collapsed, the map stays alive in the hidden container. Google bills every map INSTANCE
    // as a map load, so re-creating one per toggle would be paid for; a hidden Leaflet map costs
    // a few hundred kilobytes. Nothing is requested while hidden — applyArea() checks visible().
  }

  /** Re-expanded: the existing map measures its container again and redraws the current query. */
  private resume(): void {
    if (!this.handle) {
      this.mount();
      return;
    }
    this.handle.invalidateSize();
    this.applyArea();
  }

  /** Creates the map if needed and draws the current query — the work of every show. */
  private mount(): void {
    this.ensureMap(this.container().nativeElement).then(() => this.applyArea());
  }

  /** The list's area: a new query always starts here, viewport mode is a user gesture away. */
  private applyArea(): void {
    const query = this.query();
    if (!this.handle || !query || !this.visible()) {
      return;
    }
    this.mode.set('area');
    this.handle.setOrigin(query.origin, query.originLabel ?? '');
    const geo = this.areaOf(query);
    if (geo) {
      this.handle.fitRadius(geo.origin, geo.radiusKm);
    }
    this.pinsLoad$.next(geo);
  }

  /** The list's own geo area — a radius around the origin — or null for the plain result. */
  private areaOf(query: CatalogMapQuery): CatalogGeoQuery | null {
    return query.origin && query.radiusKm != null ? { origin: query.origin, radiusKm: query.radiusKm } : null;
  }

  private applyPins(result: CatalogPins | null): void {
    if (!result) {
      this.failed.set(true);
      this.sellerCount.set(0);
      this.truncated.set(false);
      this.pinsById = new Map();
      this.handle?.setPins([]);
      return;
    }
    this.failed.set(false);
    this.sellerCount.set(result.sellerCount);
    this.truncated.set(result.truncated);
    this.pinsById = new Map(result.pins.map((pin) => [pin.sellerCustomerId, pin]));
    this.handle?.setPins(result.pins.map(toMapPin));
    // Without a radius there is nothing to fit the map to but the pins themselves — the
    // „country overview" of the mockup; with one, the radius fit from applyArea stands.
    const query = this.query();
    if (this.mode() === 'area' && query && !this.areaOf(query)) {
      this.handle?.fitPins();
    }
  }

  private ensureMap(container: HTMLDivElement): Promise<void> {
    if (this.handle) {
      return Promise.resolve();
    }
    this.creating ??= this.adapter
      .create(container, {
        config: environment.map,
        center: GERMANY_CENTER,
        zoom: GERMANY_ZOOM,
        onViewportChange: (viewport) => this.viewport$.next(viewport),
        popupContent: (pin) => this.popupContent(pin),
      })
      .then((handle) => {
        // The toggle may have collapsed the map while the library was loading.
        if (!this.visible()) {
          handle.destroy();
          return;
        }
        this.handle = handle;
      })
      .catch((error: unknown) => {
        console.error('catalog map: the map library could not be loaded', error);
        this.failed.set(true);
      })
      .finally(() => (this.creating = null));
    return this.creating;
  }

  /** Built as DOM, not markup: the seller's name is their own text and never reaches innerHTML. */
  private popupContent(mapPin: MapPin): HTMLElement {
    const pin = this.pinsById.get(mapPin.id);
    const root = document.createElement('div');
    root.className = 'catalog-map-popup';
    root.setAttribute('data-testid', 'catalog-map-popup');

    const name = document.createElement('strong');
    name.textContent = mapPin.title;
    root.appendChild(name);

    const line = document.createElement('div');
    line.className = 'small text-muted';
    line.textContent = [this.distancePipe.transform(pin?.distanceKm), hitCountText(mapPin.count)]
      .filter(Boolean)
      .join(' · ');
    root.appendChild(line);

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'btn btn-sm btn-outline-primary mt-2';
    button.setAttribute('data-testid', 'catalog-map-popup-only-seller');
    button.textContent = $localize`:@@catalogMap.onlyThisSeller:Nur diesen Anbieter zeigen`;
    button.addEventListener('click', () => this.sellerSelected.emit({ id: mapPin.id, name: mapPin.title }));
    root.appendChild(button);
    return root;
  }

  /** The last result's pins by seller — the popup reads the distance from here. */
  private pinsById = new Map<string, CatalogPin>();

  private filter(): CatalogFilter {
    const query = this.query();
    return new CatalogFilter(query?.name ?? null, query?.categoryId ?? null, query?.productSkuId ?? null);
  }
}

function hitCountText(count: number): string {
  return count === 1
    ? $localize`:@@catalogMap.hitCount.one:1 Treffer`
    : $localize`:@@catalogMap.hitCount.other:${count}:count: Treffer`;
}

function toMapPin(pin: CatalogPin): MapPin {
  return {
    id: pin.sellerCustomerId,
    position: { latitude: pin.latitude, longitude: pin.longitude },
    precision: pin.geocodePrecision === 'postalCode' ? 'postalCode' : 'street',
    count: pin.itemCount,
    title: pin.sellerName,
  };
}

function readStoredVisibility(): boolean {
  try {
    const stored = localStorage.getItem(MAP_VISIBLE_STORAGE_KEY);
    if (stored === '1') {
      return true;
    }
    if (stored === '0') {
      return false;
    }
  } catch {
    // a private window or blocked site data: fall through to the viewport default
  }
  return typeof matchMedia === 'function' ? matchMedia(WIDE_VIEWPORT_QUERY).matches : true;
}

function writeStoredVisibility(visible: boolean): void {
  try {
    localStorage.setItem(MAP_VISIBLE_STORAGE_KEY, visible ? '1' : '0');
  } catch {
    // ignored on purpose — see readStoredVisibility
  }
}
